import { sub } from 'date-fns';

import store from '../redux/store';

import {
  getStoplightsByLocation,
  getIssuesByUnitIdsOrEmployeeList,
  getIssueReportByFacilityId,
  getFeedbackByUnitIds,
  getFeedbackByEmployeeList,
  getFeedbackReportByFacilityId
} from '../redux/actions';
import { UsersApi } from '../redux/apis';

import { date } from './helpers';

import {
  AuthUserProps,
  Employee,
  Filter,
  GetTaskParams
} from '../config/interfaces';

interface ITaskApiProps {
  authorizedUser: AuthUserProps;
  employeesToRound?: Employee[];
  searchTerm: string;
  selectedUnits: { id: string; unit: string }[];
  sortDir: string;
  sortKey: string;
  summaryView?: string;
}

interface IIssuesApiProps extends ITaskApiProps {
  currentIssuePage: number;
  issueFilter: Filter;
  selectedDelegate: string;
  selectedEmpDepartments: { departmentId: string; departmentName: string }[];
}

interface IFeedbackApiProps extends ITaskApiProps {
  currentFeedbackPage: number;
  feedbackFilter: Filter;
  selectedDelegate: string;
  selectedEmpDepartments: { departmentId: string; departmentName: string }[];
}

interface IStoplightsApiProps extends ITaskApiProps {
  currentStoplightPage: number;
  stoplightFilter: Filter;
}

export const extractEmployeeIds = async (hcaid: string) => {
  const directEmployeesToRound = await store
    .dispatch(
      UsersApi.endpoints.getEmployeesToRound.initiate(
        {
          hcaid,
          directReportsOnly: true
        },
        { forceRefetch: true }
      )
    )
    .unwrap()
    .catch(() => []);
  // const adHocEmployeesToRound = await store
  //   .dispatch(
  //     UsersApi.endpoints.getEmployeesToRound.initiate({
  //       hcaid: delegate || userId,
  //       directReportsOnly: false
  //     })
  //   )
  //   .unwrap()
  //   .catch(() => []);

  return [
    ...directEmployeesToRound
    // ...adHocEmployeesToRound
  ];
};

export const useIssuesApi = async ({
  authorizedUser,
  currentIssuePage,
  issueFilter,
  searchTerm,
  selectedDelegate,
  selectedUnits,
  selectedEmpDepartments,
  sortDir,
  sortKey,
  summaryView
}: IIssuesApiProps) => {
  const { userSection: location } = store.getState().UserReducer;
  const { empDepartments } = store.getState().ConfigReducer;

  // Issue Filter is clear
  if (Object.keys(issueFilter).length === 0) {
    const filterData: GetTaskParams = {
      page: currentIssuePage,
      sortBy: sortKey,
      sortOrder: sortDir,
      query: searchTerm ?? '',
      startDate:
        location === 'reports'
          ? summaryView === 'patient'
            ? date.formatFilterStartDate(date.convertToDayOfYear(new Date()))
            : date.formatFilterStartDate(
                date.convertToDayOfYear(
                  new Date(new Date().getFullYear(), new Date().getMonth(), 1)
                )
              )
          : date.formatFilterStartDate(
              date.convertToDayOfYear(sub(new Date(), { days: 30 }))
            )
    };
    if (location === 'patient' || summaryView === 'patient') {
      return getIssuesByUnitIdsOrEmployeeList({
        location,
        filterData: {
          ...filterData,
          facilityId: authorizedUser.facilityId ?? '',
          units: selectedUnits.map(val => val.id).join(',')
        }
      });
    }
    if (selectedDelegate === authorizedUser.facilityId) {
      return getIssueReportByFacilityId({
        location,
        filterData: {
          ...filterData,
          facilityId: authorizedUser.facilityId ?? '',
          departments: selectedEmpDepartments
            ? selectedEmpDepartments.length === empDepartments.length
              ? 'ALL'
              : selectedEmpDepartments.map(dept => dept.departmentId).join(',')
            : 'ALL'
        },
        roundingTypes: ['employee', 'cned', 'csrn']
      });
    }

    return getIssuesByUnitIdsOrEmployeeList({
      location,
      filterData: {
        ...filterData,
        userId: selectedDelegate || authorizedUser.hcaid,
        employeeIds: (
          await extractEmployeeIds(selectedDelegate || authorizedUser.hcaid)
        )
          .map((val: Employee) => val.hcaid)
          .join(',')
      }
    });
  }

  // Issue filter is applied
  const filterData = {
    page: currentIssuePage,
    sortBy: sortKey,
    sortOrder: sortDir,
    query: searchTerm
  } as GetTaskParams;
  if (location === 'patient' || summaryView === 'patient') {
    filterData.facilityId = authorizedUser.facilityId;
    filterData.units = selectedUnits.map(val => val.id).join(',');
  } else {
    filterData.facilityId = authorizedUser.facilityId;
    filterData.employeeIds = (
      await extractEmployeeIds(selectedDelegate || authorizedUser.hcaid)
    )
      .map((val: Employee) => val.hcaid)
      .join(',');
  }

  const {
    startDate,
    endDate,
    category,
    subCategory,
    createdBy,
    employeeId,
    complete,
    opened,
    attachedToStoplight,
    hasUpdate,
    patient
  } = issueFilter;
  const statuses: string[] = [];
  if (complete) {
    statuses.push('completed');
  }
  if (opened) {
    statuses.push('opened');
  }
  if (attachedToStoplight) {
    filterData.attachedToStoplight = attachedToStoplight;
  }
  if (hasUpdate) {
    filterData.hasComment = hasUpdate;
  }

  filterData.startDate = startDate
    ? date.formatFilterStartDate(startDate)
    : summaryView === 'patient'
    ? date.formatFilterStartDate(date.convertToDayOfYear(new Date()))
    : summaryView === 'employee'
    ? date.formatFilterStartDate(
        date.convertToDayOfYear(
          new Date(new Date().getFullYear(), new Date().getMonth(), 1)
        )
      )
    : date.formatFilterStartDate(
        date.convertToDayOfYear(sub(new Date(), { days: 30 }))
      );
  filterData.endDate = endDate
    ? date.formatFilterEndDate(endDate)
    : date.formatFilterEndDate(date.convertToDayOfYear(new Date()));

  if (location === 'patient' || summaryView === 'patient') {
    filterData.category = category;
    filterData.subCategory = subCategory;
    filterData.createdBy = createdBy;
    filterData.patientName = patient;
    filterData.status = statuses;

    return getIssuesByUnitIdsOrEmployeeList({
      location,
      filterData
    });
  }
  if (selectedDelegate === authorizedUser.facilityId) {
    delete filterData.employeeIds;
    return getIssueReportByFacilityId({
      location,
      filterData: {
        ...filterData,
        facilityId: authorizedUser.facilityId,
        departments: selectedEmpDepartments
          ? selectedEmpDepartments.length === empDepartments.length
            ? 'ALL'
            : selectedEmpDepartments.map(dept => dept.departmentId).join(',')
          : 'ALL',
        category,
        subCategory,
        createdBy,
        employeeId,
        status: statuses
      },
      roundingTypes: ['employee', 'cned', 'csrn']
    });
  }
  filterData.category = category;
  filterData.subCategory = subCategory;
  filterData.createdBy = createdBy;
  filterData.employeeId = employeeId;
  filterData.status = statuses;
  filterData.userId = selectedDelegate || authorizedUser.hcaid;
  return getIssuesByUnitIdsOrEmployeeList({
    location,
    filterData
  });
};

export const useFeedbackApi = async ({
  authorizedUser,
  currentFeedbackPage,
  feedbackFilter,
  searchTerm,
  selectedDelegate,
  selectedUnits,
  selectedEmpDepartments,
  sortKey,
  sortDir,
  summaryView
}: IFeedbackApiProps) => {
  const { userSection: location } = store.getState().UserReducer;
  const { empDepartments } = store.getState().ConfigReducer;

  // Feedback filter is clear
  if (Object.keys(feedbackFilter).length === 0) {
    const filterData = {
      sortOrder: sortDir,
      sortBy: sortKey,
      limit: 50,
      page: currentFeedbackPage,
      startDate:
        location === 'reports' && summaryView === 'patient'
          ? date.formatFilterStartDate(date.convertToDayOfYear(new Date()))
          : date.formatFilterStartDate(
              date.convertToDayOfYear(sub(new Date(), { days: 30 }))
            ),
      query: searchTerm
    };
    if (location === 'patient' || summaryView === 'patient') {
      return getFeedbackByUnitIds({
        location,
        filterData: {
          ...filterData,
          facilityId: authorizedUser.facilityId,
          units: selectedUnits.map(item => item.id).join(',')
        }
      });
    }

    // For feedback list based on facility
    const filterObj = {
      ...filterData,
      facilityId: selectedDelegate,
      departments: selectedEmpDepartments
        ? selectedEmpDepartments.length === empDepartments.length
          ? 'ALL'
          : selectedEmpDepartments.map(dept => dept.departmentId).join(',')
        : 'ALL'
    };

    if (selectedDelegate === authorizedUser.facilityId) {
      return getFeedbackReportByFacilityId({
        location,
        filterData: filterObj,
        roundingTypes: ['employee', 'cned', 'csrn']
      });
    }

    const employeesToRound = await extractEmployeeIds(
      selectedDelegate || authorizedUser.hcaid
    );

    // TODO: This is causing issues
    if (employeesToRound.length === 0) {
      return getFeedbackByEmployeeList({
        ...filterData,
        employeeIds: selectedDelegate || authorizedUser.hcaid,
        userId: selectedDelegate || authorizedUser.hcaid
      });
    }
    return getFeedbackByEmployeeList({
      ...filterData,
      userId: selectedDelegate || authorizedUser.hcaid,
      employeeIds: employeesToRound.map((item: Employee) => item.hcaid)
    });
  }

  // Feedback filter is applied
  const {
    startDate,
    endDate,
    category,
    subCategory,
    createdBy,
    patient,
    employeeId,
    complete,
    opened,
    taskSubType
  } = feedbackFilter;
  const statuses = [];
  if (complete) {
    statuses.push('completed');
  }
  if (opened) {
    statuses.push('opened');
  }

  if (location === 'patient') {
    return getFeedbackByUnitIds({
      location,
      filterData: {
        query: searchTerm,
        facilityId: authorizedUser.facilityId,
        units: selectedUnits.map(item => item.id).join(','),
        startDate: startDate
          ? date.formatFilterStartDate(startDate)
          : date.formatFilterStartDate(date.convertToDayOfYear(new Date())),
        endDate: endDate
          ? date.formatFilterEndDate(endDate)
          : date.formatFilterEndDate(date.convertToDayOfYear(new Date())),
        patientName: patient,
        searchEmployeeId: employeeId,
        category,
        subCategory,
        createdBy,
        status: statuses,
        page: currentFeedbackPage,
        sortBy: sortKey,
        sortOrder: sortDir,
        taskSubType
      }
    });
  }

  // For feedback call coming from summary report view since most filters do not apply
  if (summaryView === 'patient') {
    return getFeedbackByUnitIds({
      location,
      filterData: {
        query: searchTerm,
        facilityId: authorizedUser.facilityId,
        units: selectedUnits.map(item => item.id).join(','),
        startDate: startDate
          ? date.formatFilterStartDate(startDate)
          : date.formatFilterStartDate(date.convertToDayOfYear(new Date())),
        endDate: endDate
          ? date.formatFilterEndDate(endDate)
          : date.formatFilterEndDate(date.convertToDayOfYear(new Date())),
        page: currentFeedbackPage,
        sortBy: sortKey,
        sortOrder: sortDir,
        taskSubType
      }
    });
  }
  if (summaryView === 'employee') {
    return getFeedbackReportByFacilityId({
      location,
      filterData: {
        facilityId: authorizedUser.facilityId,
        departments: selectedEmpDepartments
          ? selectedEmpDepartments.length === empDepartments.length
            ? 'ALL'
            : selectedEmpDepartments.map(dept => dept.departmentId).join(',')
          : 'ALL',
        startDate: startDate
          ? date.formatFilterStartDate(startDate)
          : date.formatFilterStartDate(
              date.convertToDayOfYear(
                new Date(new Date().getFullYear(), new Date().getMonth(), 1)
              )
            ),
        endDate: endDate
          ? date.formatFilterEndDate(endDate)
          : date.formatFilterEndDate(date.convertToDayOfYear(new Date())),
        page: currentFeedbackPage,
        sortBy: sortKey,
        sortOrder: sortDir,
        taskSubtype: taskSubType
      },
      roundingTypes: ['employee', 'cned', 'csrn']
    });
  }

  const filterObj = {
    query: searchTerm,
    startDate: startDate
      ? date.formatFilterStartDate(startDate)
      : date.formatFilterStartDate(
          date.convertToDayOfYear(
            new Date(new Date().getFullYear(), new Date().getMonth(), 1)
          )
        ),
    endDate: endDate
      ? date.formatFilterEndDate(endDate)
      : date.formatFilterEndDate(date.convertToDayOfYear(new Date())),
    category,
    subCategory,
    createdBy,
    status: statuses,
    page: currentFeedbackPage,
    sortBy: sortKey,
    sortOrder: sortDir,
    taskSubtype: taskSubType
  };
  if (selectedDelegate === authorizedUser.facilityId) {
    return getFeedbackReportByFacilityId({
      location,
      filterData: {
        ...filterObj,
        facilityId: [authorizedUser.facilityId],
        departments: selectedEmpDepartments
          ? selectedEmpDepartments.length === empDepartments.length
            ? 'ALL'
            : selectedEmpDepartments.map(dept => dept.departmentId).join(',')
          : 'ALL'
      },
      roundingTypes: ['employee', 'cned', 'csrn']
    });
  }

  return getFeedbackByEmployeeList({
    ...filterObj,
    userId: selectedDelegate || authorizedUser.hcaid,
    employeeIds: (
      await extractEmployeeIds(selectedDelegate || authorizedUser.hcaid)
    ).map((item: Employee) => item.hcaid)
  });
};

export const useStoplightsApi = ({
  authorizedUser,
  currentStoplightPage,
  searchTerm,
  selectedUnits,
  sortKey,
  sortDir,
  stoplightFilter,
  summaryView
}: IStoplightsApiProps) => {
  const { userSection: location } = store.getState().UserReducer;

  // Stoplight filter is clear
  if (Object.keys(stoplightFilter).length === 0) {
    if (location === 'patient' || summaryView === 'patient') {
      return getStoplightsByLocation({
        location,
        filterData: {
          facilityId: authorizedUser.facilityId,
          locationIds: selectedUnits.map(item => item.id),
          locationType: 'unit',
          origin: 'patient',
          page: currentStoplightPage,
          sortBy: sortKey,
          sortOrder: sortDir,
          query: searchTerm || '',
          startDate:
            location === 'reports'
              ? date.formatFilterStartDate(date.convertToDayOfYear(new Date()))
              : date.formatFilterStartDate(
                  date.convertToDayOfYear(sub(new Date(), { days: 30 }))
                )
        }
      });
    }
    return getStoplightsByLocation({
      location,
      filterData: {
        facilityId: authorizedUser.facilityId,
        locationIds: [authorizedUser.facilityId],
        locationType: 'facility',
        origin: 'employee',
        page: currentStoplightPage,
        sortBy: sortKey,
        sortOrder: sortDir,
        query: searchTerm || '',
        startDate: date.formatFilterStartDate(
          date.convertToDayOfYear(
            new Date(new Date().getFullYear(), new Date().getMonth(), 1)
          )
        )
      }
    });
  }

  // Stoplight filter is applied
  const {
    startDate,
    endDate,
    category,
    subCategory,
    createdBy,
    complete,
    opened,
    deferred,
    hasAttachedIssues,
    hasUpdate
  } = stoplightFilter;
  let statuses = [];
  if (complete) {
    statuses.push('completed');
  }
  if (opened) {
    statuses.push('inProgress');
  }
  if (deferred) {
    statuses.push('deferred');
  }
  if (!complete && !opened && !deferred) {
    statuses = ['completed', 'deferred', 'inProgress'];
  }

  if (location === 'patient' || summaryView === 'patient') {
    return getStoplightsByLocation({
      location,
      filterData: {
        locationIds: selectedUnits.map(item => item.id),
        facilityId: authorizedUser.facilityId,
        locationType: 'unit',
        origin: 'patient',
        startDate: startDate
          ? date.formatFilterStartDate(startDate)
          : location === 'reports'
          ? date.formatFilterStartDate(date.convertToDayOfYear(new Date()))
          : date.formatFilterStartDate(
              date.convertToDayOfYear(sub(new Date(), { days: 30 }))
            ),
        endDate: endDate
          ? date.formatFilterEndDate(endDate)
          : date.formatFilterEndDate(date.convertToDayOfYear(new Date())),
        createdBy,
        category,
        subCategory,
        hasIssues: hasAttachedIssues,
        hasComment: hasUpdate,
        status: statuses,
        page: currentStoplightPage,
        sortBy: sortKey,
        sortOrder: sortDir,
        query: searchTerm || ''
      }
    });
  }
  return getStoplightsByLocation({
    location,
    filterData: {
      locationIds: [authorizedUser.facilityId],
      locationType: 'facility',
      origin: 'employee',
      startDate: startDate
        ? date.formatFilterStartDate(startDate)
        : summaryView === 'employee'
        ? date.formatFilterStartDate(
            date.convertToDayOfYear(
              new Date(new Date().getFullYear(), new Date().getMonth(), 1)
            )
          )
        : date.formatFilterStartDate(
            date.convertToDayOfYear(sub(new Date(), { days: 30 }))
          ),
      endDate: endDate
        ? date.formatFilterEndDate(endDate)
        : date.formatFilterEndDate(date.convertToDayOfYear(new Date())),
      createdBy,
      category,
      subCategory,
      hasIssues: hasAttachedIssues,
      hasComment: hasUpdate,
      status: statuses,
      page: currentStoplightPage,
      sortBy: sortKey,
      sortOrder: sortDir,
      query: searchTerm || '',
      facilityId: authorizedUser.facilityId
    }
  });
};
